import { isEmpty, isNull } from 'lodash';
import moment from 'moment';
import { formIsValid } from 'utils/validation';
import { validateText } from 'utils/validation/fields';
import {
  FORM_ACTION_TYPES,
  FORM_REQUIRED_FIELDS_BASE,
  FORM_REQUIRED_FIELDS,
  formInitialState,
  formNames,
} from './constants';

export const init = (initialState) => {
  return { ...initialState };
};
const { dateStart, dateEnd, formIds, agencyIds } = formNames;
const DATE_FIELDS = [dateStart, dateEnd];

export const isEmptyOrAllFalse = (obj) =>
  Object.keys(obj).length === 0 || Object.values(obj).every((value) => value === false);

const getErrors = (data, errors, name) => {
  const start = data[dateStart];
  const end = data[dateEnd];

  if (!DATE_FIELDS.includes(name)) {
    return {
      ...errors,
      [name]: false,
    };
  }

  if (moment(start).isAfter(moment(end))) {
    return {
      [dateEnd]: false,
      [dateStart]: 'Start date cannot be greater than end date',
    };
  }

  if (!!start && !!end && !moment(start).isAfter(moment(end))) {
    return { ...errors, [dateStart]: false, [dateEnd]: false };
  }

  if (isNull(end) && name === dateEnd && !errors[name]) {
    return {
      ...errors,
      [dateEnd]: 'End date is required',
    };
  }
  if (isNull(start) && name === dateStart) {
    return {
      ...errors,
      [dateStart]: 'Start date is required',
    };
  }
  if (errors[name] && !data[name]) {
    return { ...errors };
  }

  return {
    ...errors,
    [name]: false,
  };
};

export const validateData = (action) => {
  const value = action.payload?.value ? action.payload?.value : action.payload;

  switch (action.name) {
    case dateStart: {
      return isNull(action.payload) ? 'Start date is required' : false;
    }

    case dateEnd: {
      return isNull(action.payload) ? 'End date is required' : false;
    }

    case formIds: {
      return isEmpty(action.payload) ? 'Form is required' : false;
    }

    case agencyIds: {
      return isEmpty(action.payload) ? 'Agency is required' : false;
    }

    default:
      return validateText({
        value,
        name: 'field',
        required: FORM_REQUIRED_FIELDS.includes(action.name),
      });
  }
};

const checkFormIsValid = (errors) => formIsValid(errors, FORM_REQUIRED_FIELDS);

const validateFormData = (data, requiredFields) =>
  requiredFields.reduce((acc, name) => {
    acc[name] = validateData({
      payload: data[name],
      name,
    });
    return acc;
  }, {});

export const formReducer = (formState, action) => {
  switch (action.type) {
    case FORM_ACTION_TYPES.VALIDATE_DATA: {
      const { data } = formState;
      const requiredFields = [
        ...new Set([
          ...(action.shortForm ? FORM_REQUIRED_FIELDS_BASE : FORM_REQUIRED_FIELDS),
          ...(action.requiredFields || []),
        ]),
      ];

      const newErrors = validateFormData(data, requiredFields);

      return {
        ...formState,
        errors: newErrors,
        canBeSubmitted: checkFormIsValid(newErrors),
      };
    }

    case FORM_ACTION_TYPES.ENTER_DATA: {
      const requiredFields = [
        ...new Set([
          ...(action.shortForm ? FORM_REQUIRED_FIELDS_BASE : FORM_REQUIRED_FIELDS),
          ...(action.requiredFields || []),
        ]),
      ];
      const newData = {
        ...formState.data,
        [action.name]: action.payload,
      };

      const testErrors = validateFormData(newData, [action.name]);
      const newErrors = getErrors(newData, formState.errors, action.name);

      const shadowErrors = validateFormData(newData, requiredFields);

      return {
        ...formState,
        data: newData,
        errors: { ...testErrors, ...newErrors },
        canBeSubmitted: checkFormIsValid(shadowErrors),
      };
    }

    case FORM_ACTION_TYPES.SET_ERROR: {
      const newErrors = {
        ...formState.errors,
        [action.name]: action.payload,
      };

      return {
        ...formState,
        errors: newErrors,
        canBeSubmitted: checkFormIsValid(newErrors),
      };
    }

    case FORM_ACTION_TYPES.SET_INVALID_DATE_ERROR: {
      const newData = {
        ...formState.data,
        [action.name]: null,
      };

      const newErrors = {
        ...formState.errors,
        [action.name]: action.payload,
      };

      return {
        ...formState,
        data: newData,
        errors: newErrors,
        canBeSubmitted: action.payload ? false : checkFormIsValid(newErrors),
      };
    }

    case FORM_ACTION_TYPES.CHANGE_DATE: {
      const newData = { ...formState.data, [formIds]: null, [agencyIds]: null };

      const requiredFields = [
        ...new Set([
          ...(action.shortForm ? FORM_REQUIRED_FIELDS_BASE : FORM_REQUIRED_FIELDS),
          ...(action.requiredFields || []),
        ]),
      ];
      const newErrors = validateFormData(newData, requiredFields);
      return {
        ...formState,
        data: newData,
        canBeSubmitted: checkFormIsValid(newErrors),
      };
    }

    case FORM_ACTION_TYPES.RESET_DATA: {
      return {
        ...formInitialState,
      };
    }

    default:
      return {
        ...formInitialState,
      };
  }
};
